import axios, { AxiosError } from 'axios';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { useGetLoginUserInfo, useGetLoginUserprivilege } from '../../../../api/auth/auth';
import { ErrorInfo, ReplaceImgSrc, Section, SectionPrivilage } from '../../../../api/model';
import { useGetPsNoteList } from '../../../../api/ps-notes/ps-notes';
import {
  useGetSection,
  usePostSectionSave,
  usePostSectionsLogcheck,
  usePostSectionsLogdownload,
} from '../../../../api/sections/sections';
import { CKEDITOR_WIDTHS } from '../../../../constants/ckeditor';
import { useDefaultOptions, useOptionsForBinary } from '../../../../hooks/api';
import { getAxiosRequestConfig } from '../../../../utils/api';
import { executeDownload } from '../../../../utils/download';
import * as sessionStorageUtils from '../../../../utils/sessionStorage';
import BlobErrorModal from '../../../modules/BlobErrorModal';
import ConfirmModal from '../../../modules/ConfirmModal';
import ErrorModal from '../../../modules/ErrorModal';
import SpinnerModal from '../../../modules/SpinnerModal';
import DownloadCommand from './DownloadCommand';
import PreviewCommand from './PreviewCommand';
import PsDiffCommand from './psdiff/PsDiffCommand';
import { PsNoteFormContainer } from './psnote/PsNoteFormContainer';
import ReqSpecImportModal from './ReqSpecImportModal';
import RevisionCommand from './RevisionCommand';
import RevisionPreviewDiff from './RevisionPreviewDiff';
import RevisionSaveModal from './RevisionSaveModal';
import { SectionEditor } from './SectionEditor';

type Props = {
  sectionId: number;
  sectionPrivilege: SectionPrivilage | undefined;
  productionDivisionId: number;
  setInitSection: (section: Section) => void;
  setIsEdit: (isEdit: boolean) => void;
  setSecReading: (secReading: boolean) => void;
};
function SectionMain({
  sectionId,
  sectionPrivilege,
  productionDivisionId,
  setInitSection,
  setIsEdit,
  setSecReading,
}: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const options = useDefaultOptions(language);
  const { productionId } = useParams();
  const [isCheckedIncludeVisual, setIsCheckedIncludeVisual] = useState(true);
  const [isCheckedIncludeIndex, setIsCheckedIncludeIndex] = useState(true);
  const [isDisplayIncludeVisual, setIsDisplayIncludeVisual] = useState(false);
  const [isDisplayIncludeIndex, setIsDisplayIncludeIndex] = useState(false);
  const [isPsNote, setIsPsNote] = useState(false);
  const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isNotCommentSaved, setIsNotCommentSaved] = useState(false);
  const [notCommentSavedElementsLength, setNotCommentSavedElementsLength] = useState(0);
  const [psDerivationId, setPsDerivationId] = useState<number | null>(null);
  const handleClosePsNote = () => {
    setIsPsNote(false);
  };

  // 検索条件 章
  const defaultCharterNumberId = sessionStorageUtils.get('psNote.ChapterNumberId', 'all'); // セッションから取得(無ければ'')
  const [selectedCharterNumberId, setSelectedCharterNumberId] = useState(defaultCharterNumberId);
  const chapterNumberId = selectedCharterNumberId === '' ? 'all' : selectedCharterNumberId;

  // 改廃案メモ一覧取得
  const { data: psNoteData, refetch: psNoteRefetch } = useGetPsNoteList(Number(productionId), chapterNumberId, options);
  const psNotes = useMemo(() => psNoteData?.data.ps_notes ?? [], [psNoteData?.data.ps_notes]);

  const handleOpenPsNote = () => {
    if (psDerivationId) {
      setIsPsNote(true);
    }
  };

  const [displayRevisionSaveModal, setDisplayRevisionSaveModal] = useState(false);

  // ReqSPEC連携インポート モーダル管理
  const [showReqSpecImportModal, setShowReqSpecImportModal] = useState(false);
  const openReqSpecImportModal = () => {
    setShowReqSpecImportModal(true);
  };

  const [params] = useSearchParams();
  const ref = useRef(null);
  const { data, refetch } = useGetSection(sectionId, options);
  const section = data?.data;

  useEffect(() => {
    if (params.get('section_id') && section) {
      setInitSection(section);
    }
  }, [setInitSection, params, section]);

  const [editor, setEditor] = useState<any>(null);
  const handleSetEditor = (value: any) => {
    setIsEdit(true);
    setEditor(value);
  };
  const {
    mutateAsync: postSectionMutateAsync,
    isSuccess,
    error: saveError,
  } = usePostSectionSave<AxiosError<ErrorInfo>, unknown>(options);

  // 保存処理
  const handleSave = async (revisionTitle: string, commentCheck = true) => {
    // 未保存コメントのチェック
    setIsNotCommentSaved(false);
    if (commentCheck === true) {
      const notCommentSavedElements = document.querySelectorAll(
        '.ck-thread__container .ck-editor__editable[role="textbox"] p'
      );
      const filteredNotCommentSavedElements = Array.from(notCommentSavedElements).filter(
        (element) => element.textContent !== ''
      );
      setNotCommentSavedElementsLength(filteredNotCommentSavedElements.length);

      if (filteredNotCommentSavedElements.length > 0) {
        setIsNotCommentSaved(true);
        return;
      }
    }

    setDisplaySpinnerModal(true);

    // --- ID付与の処理 ここから -----------------------
    interface IBlockElements {
      [key: string]: string;
    }
    // ブロック要素： div, p, h1～h5, figure, img（本来インライン要素だが何故かこっち）
    /* eslint-disable */
    let blockElements: IBlockElements = {
      htmlDivParagraph: 'htmlDivAttributes',
      paragraph: 'htmlPAttributes',
      heading1: 'htmlH1Attributes',
      heading2: 'htmlH2Attributes',
      heading3: 'htmlH3Attributes',
      heading4: 'htmlH4Attributes',
      heading5: 'htmlH5Attributes',
    };

    // 見出しで使われる要素名
    const headingElements = {
      paragraphSupplementSubhead: 'htmlPAttributes',
      paragraphSupplementPara: 'htmlPAttributes',
      paragraphReferGear: 'htmlPAttributes',
      paragraphRefer1: 'htmlPAttributes',
      allColumnIllust: 'htmlPAttributes',
      paragraphBlank: 'htmlPAttributes',
      verbatim: 'htmlPAttributes',
      warningListTitle: 'htmlPAttributes',
      illustHeadline: 'htmlPAttributes',
      customGroup: 'htmlPAttributes',
      customItem: 'htmlPAttributes',
    };

    let repImgSrcArr: ReplaceImgSrc[] = [];

    // チェックにはblockElementsを使用
    Object.assign(blockElements, headingElements);

    // インライン要素：a,
    const inlineElements = {
      htmlA: '',
    };

    // idの発行回数
    let cntId = 0;

    // idを発行
    const getUniqueId = function () {
      const dt = new Date();
      let id =
        '#' +
        ('00' + dt.getHours()).slice(-2) +
        ('00' + (dt.getMinutes() + 1)).slice(-2) +
        ('00' + dt.getSeconds()).slice(-2) +
        ('000' + (dt.getMilliseconds() + cntId)).slice(-3);
      cntId += 1;
      return id;
    };

    // タグを遡って検索
    const searchElement = function (writer: any, element: any) {
      // console.log('searchElement element:', element)
      if (!element) return;
      if (element._children && element._children._nodes.length > 0) {
        // 子要素あり
        let tmpId: string = '';
        element._children._nodes.forEach((element: any) => {
          // イメージ要素にclass属性値が付く場合があるので、class属性を除く
          if (element.name) {
            if (element.name == 'imageInline' || element.name == 'imageBlock') {
              procAttributeImgTag(writer, element);
            }
          }

          if (!element.name && element._attrs.size > 0) {
            // インライン要素(TEXT)
            // これにはa要素やspan要素も含まれる
            // 全てに同じID属性をセットすれば、不具合を避けられる
            if (tmpId === '') {
              const sameId = getUniqueId();
              tmpId = addIdAttr(writer, element, sameId);
            } else {
              tmpId = addIdAttr(writer, element, tmpId);
            }
          } else {
            tmpId = addIdAttr(writer, element, '');
          }
          searchElement(writer, element);
        });
        return;
      } else {
        // 子要素なし
        return;
      }
    };

    // id属性を追加
    const addIdAttr = function (writer: any, element: any, sameId: string): string {
      // name属性が無い場合、Text（インライン要素）の可能性
      if (!element.name) {
        if (element._attrs.size > 0) {
          // console.log('--- インライン要素', element)
          if (element.getAttribute('linkHref') && !element.getAttribute('htmlA')) {
            // console.log('-- linkHref あり htmlA なし')
            writer.setAttribute('htmlA', { attributes: { id: sameId } }, element);

            // } else if (element.getAttribute('linkHref') && element.getAttribute('htmlA')) {
          } else if (element.getAttribute('htmlA')) {
            // console.log('-- htmlA あり')
            let aTag = element.getAttribute('htmlA');
            if (aTag.attributes) {
              // console.log('-- attributes あり')
              if (!aTag.attributes.id) {
                // console.log('-- attributes.id がない')
                const curAttr = JSON.stringify(element._attrs.get('htmlA').attributes);
                // console.log('curAttr:', curAttr)
                // const chgText = ', "id": "' + getUniqueId() + '"}'
                let chgText = '';
                if (curAttr === '{}') {
                  chgText = '"id": "' + sameId + '"}';
                } else {
                  chgText = ', "id": "' + sameId + '"}';
                }

                // console.log('chgText:', chgText)
                let strJson = curAttr.replace('}', chgText);
                // console.log('strJson:', strJson)
                strJson = '{"attributes": ' + strJson + '}';
                // console.log('strJson:', strJson)
                writer.setAttribute('htmlA', JSON.parse(strJson), element);
              }

              /*
              // aタグのhref調整
              //   hrefの先頭に#がない場合に自動で補完 (テスト用に作成したので、必要になれば生かす)
              if (element.getAttribute('linkHref')) {
                let a_href = element.getAttribute('linkHref');
                if (a_href.substr(0, 1) != '#') {
                  console.log('href属性値の先頭に#を付与');
                  writer.setAttribute('linkHref', '#' + a_href, element);
                }
              }
              */
            } else {
              // console.log('-- htmlA.attributes なし')
              writer.setAttribute('htmlA', { attributes: { id: sameId } }, element);
            }
          }
          return sameId;
        }
        return '';
      }

      // liタグの処理
      if (element.name === 'paragraph' && element._attrs && element._attrs.size > 0) {
        // listItemId があれば liタグかもしれない
        if (element._attrs.get('listItemId')) {
          if (element._attrs.get('htmlPAttributes')) {
            // console.log('-- htmlPAttributes がある:', element._attrs.get('htmlPAttributes'))

            if (element._attrs.get('htmlPAttributes').attributes) {
              // console.log('-- p.attributes がある:', element._attrs.get('htmlPAttributes').attributes)
              if (!element._attrs.get('htmlPAttributes').attributes.id) {
                // console.log('-- p.attributes.id がない:', element._attrs.get('htmlPAttributes').attributes)

                const curAttr = JSON.stringify(element._attrs.get('htmlPAttributes'));
                let strJson = '';
                // if (curAttr === '"attributes":{}') {
                if (curAttr.indexOf('"attributes":{}') != -1) {
                  strJson = curAttr.replace('"attributes":{}', '"attributes":{"id": "' + getUniqueId() + '"}');
                } else {
                  strJson = curAttr.replace('"attributes":{', '"attributes":{"id": "' + getUniqueId() + '", ');
                }
                console.log('--- strJson:', strJson);

                writer.setAttribute('htmlPAttributes', JSON.parse(strJson), element);
              } else {
                // console.log('-- p.attributes.id がある:', element._attrs.get('htmlPAttributes').attributes.id)
              }
            } else {
              // console.log('-- p.attributes がない')
              const curAttr = JSON.stringify(element._attrs.get('htmlPAttributes'));
              let chgText = '';
              if (curAttr === '{}') {
                chgText = '"id": "' + getUniqueId() + '"}';
              } else {
                chgText = ', "id": "' + getUniqueId() + '"}';
              }
              let strJson = curAttr.replace('}', chgText);
              strJson = '{"attributes": ' + strJson + '}';
              writer.setAttribute('htmlPAttributes', JSON.parse(strJson), element);
              // writer.setAttribute( 'htmlLiAttributes', {'attributes': {'id': getUniqueId()}}, element );
            }
          } else {
            // console.log('-- htmlPAttributes がない:', element._attrs.get('htmlPAttributes'))
            writer.setAttribute('htmlPAttributes', { attributes: { id: getUniqueId() } }, element);
          }
        }
      }

      // Element（ブロック要素） の処理
      if (element.name in blockElements) {
        if (element._attrs.size <= 0) {
          // console.log('--- element.属性なし')
          writer.setAttribute(blockElements[element.name], { attributes: { id: getUniqueId() } }, element);
        } else {
          // console.log('--- 属性あり')

          // 特殊ケース
          // <figure> タグの中に <img> タグがある場合、imgタグの識別ができない
          if (element.name === 'imageBlock') {
            if (element._attrs.get('src')) {
              // src属性があれば <img> タグがある可能性
              if (element._attrs.get('htmlImgAttributes')) {
                // htmlImgAttributes属性が存在する場合、id属性が既に存在しない場合、id属性を追加
                if (!element._attrs.get('htmlImgAttributes').attributes.id) {
                  // console.log('--- figure.element.htmlImgAttributesあり')
                  writer.setAttribute('htmlImgAttributes', { attributes: { id: getUniqueId() } }, element);
                }
              } else {
                // console.log('--- figure.element.htmlImgAttributesなし')
                // htmlImgAttributes属性が存在しない場合、id属性を追加する
                writer.setAttribute('htmlImgAttributes', { attributes: { id: getUniqueId() } }, element);
              }
            }
          }

          let nodeAttributes = element.getAttribute(blockElements[element.name]);
          if (nodeAttributes === undefined) {
            // 属性が何もない場合、htmlPAttributes と htmlPAttributes.attributes.id を追加する
            nodeAttributes = { [blockElements[element.name]]: { attributes: { id: getUniqueId() } } };
            writer.setAttributes(nodeAttributes, element);
          } else {
            if ('attributes' in nodeAttributes) {
              // htmlPAttributes.attributes は存在するが id が存在しない場合、id を追加する
              if (!('id' in nodeAttributes.attributes)) {
                nodeAttributes.attributes['id'] = getUniqueId();
              }
            } else {
              // htmlPAttributes は存在するが attributes が存在しない場合、attributes.id を追加する
              nodeAttributes['attributes'] = { id: getUniqueId() };
            }
          }
        }
      }
      return '';
    };

    /**
     * imgタグの操作
     *    ■ imgタグからclass属性を除く
     *    ■ imgタグのsrc属性値にキャッシュ用の文字(?time=XXXXXXXX)があれば削除
     *    ■ imgタグのsrc属性値に自資料以外が参照されている場合、属性値を調整する
     * @param writer
     * @param element
     */
    const procAttributeImgTag = (writer: any, element: any) => {
      if (element._attrs.size > 0) {
        // ■ imgタグからclass属性を除く
        if (element.getAttribute('htmlImgAttributes')) {
          let imgAttr = element.getAttribute('htmlImgAttributes');
          if (imgAttr.classes) {
            writer.removeAttribute('htmlImgAttributes', element); // htmlImgAttributesのクラス属性があるので削除
          }
        }

        // src属性がある場合
        if (element.getAttribute('src')) {
          const repImgSrcItem: ReplaceImgSrc = {};

          let imgSrcOrg = element.getAttribute('src'); // src属性値取得
          let imgSrc = imgSrcOrg;

          // ■ src属性値にキャッシュ用の文字(?time=XXXXXXXX)があれば削除
          let srcArr = imgSrcOrg.split('?'); // キャッシュ用文字列で分解
          if (srcArr.length > 1) {
            imgSrc = srcArr[0]; // キャッシュ用文字列を除いたものをセット
          }

          // ■ src属性値に自資料以外が参照されている場合、調整する
          if (imgSrc.includes('/contents/common')) {
            // 共通画像なので何もしない
          } else if (imgSrc.includes('/contents/ps/release/')) {
            // PSなので自資料のパスに直す
            let psSrcArr = imgSrc.split('/contents/ps/release/');
            if (psSrcArr.length > 1) {
              repImgSrcItem.from_src = imgSrc; // 既存値セット

              let srcHead = psSrcArr[0]; // 「/ori/s3」
              let srcFoot = psSrcArr[1];
              let srcFootArr = srcFoot.split('/');
              let fileName = srcFootArr[srcFootArr.length - 1]; // ファイル名
              let folderName = srcFoot.replace('/' + fileName, ''); // フォルダ名

              // src属性値の修正値
              imgSrc = srcHead + '/contents/production/' + productionId + '/illust/ps/' + folderName + '/' + fileName;
              repImgSrcItem.to_src = imgSrc; // 修正値セット
              repImgSrcArr.push(repImgSrcItem); // 配列に追加
            }
          } else {
            // それ以外では別の資料管理Noの画像を参照しているか確認
            let prSrcArr = imgSrc.split('/contents/production/');
            if (prSrcArr.length > 1) {
              let prSrcFoot = prSrcArr[1];
              let prSrcFootArr = prSrcFoot.split('/');
              if (prSrcFootArr[0] != productionId) {
                // 別の資料を参照してる
                repImgSrcItem.from_src = imgSrc; // 既存値セット

                // src属性値の修正値
                imgSrc = imgSrc.replace(
                  '/contents/production/' + prSrcFootArr[0] + '/',
                  '/contents/production/' + productionId + '/'
                );
                repImgSrcItem.to_src = imgSrc; // 修正値セット
                repImgSrcArr.push(repImgSrcItem); // 配列に追加
              }
            }
          }

          // src属性値が初期の値と異なっていたら、タグの属性値を更新する
          if (imgSrc != imgSrcOrg) {
            writer.setAttribute('src', imgSrc, element);
          }
        }
      }
    };

    // タグを遡ってImgタグを検索
    const searchImgElement = function (writer: any, element: any) {
      if (!element) return;
      if (element._children && element._children._nodes.length > 0) {
        // 子要素あり
        element._children._nodes.forEach((element: any) => {
          // イメージ要素にclass属性値が付く場合があるので、class属性を除く
          if (element.name) {
            if (element.name == 'imageInline' || element.name == 'imageBlock') {
              procReverseAttributeImgTag(writer, element);
            }
          }
          searchImgElement(writer, element);
        });
        return;
      } else {
        // 子要素なし
        return;
      }
    };

    /**
     * imgタグの操作（保存エラー時）
     *    バックエンドからエラーが返ってきた場合、src属性値を調整したものを戻す
     * @param writer
     * @param element
     */
    const procReverseAttributeImgTag = (writer: any, element: any) => {
      if (element._attrs.size > 0) {
        // src属性がある場合
        if (element.getAttribute('src')) {
          repImgSrcArr.forEach((item: ReplaceImgSrc) => {
            let imgSrc = element.getAttribute('src'); // src属性値取得
            if (imgSrc == item.to_src) {
              writer.setAttribute('src', item.from_src, element);
            }
          });
        }
      }
    };

    // 保存処理エラーの後のimgタグ操作
    const reverseImgSrc = () => {
      editor.model.change((writer: any) => {
        const contents = editor.model.document.getRoot();
        contents._children._nodes.forEach((element: any) => {
          searchImgElement(writer, element);
        });
      });
    };

    // タグの操作
    editor.model.change((writer: any) => {
      const contents = editor.model.document.getRoot();
      contents._children._nodes.forEach((element: any) => {
        addIdAttr(writer, element, '');
        searchElement(writer, element);
      });
    });
    /* eslint-enable */
    // --- ID付与の処理 ここまで -----------------------

    /* eslint @typescript-eslint/no-unsafe-call: off */
    const editorData = editor.data.get();
    const revisionsRepositoryPlugin = editor.plugins.get('RevisionsRepository');
    const comments = editor.plugins.get('CommentsRepository');
    const revisionTracker = editor.plugins.get('RevisionTracker');
    await revisionTracker.update();
    let revisionsData = revisionsRepositoryPlugin.getRevisions({
      toJSON: true,
    });

    const commentThreadsData = comments.getCommentThreads({
      skipNotAttached: true,
      skipEmpty: true,
      toJSON: true,
    });

    // コメント箇所の本文を削除した場合、コメントデータが残るケースの対処
    /* eslint-disable */
    for (let i = 0; i < commentThreadsData.length; i++) {
      if (commentThreadsData[i].resolvedAt === null) {
      } else {
        commentThreadsData.splice(i, 1);
      }
    }

    /* eslint-disable */
    /* comments.getCommentThreadsで取得したデータには完了フラグが付いていないので、ドキュメントから取得する? */
    commentThreadsData.forEach((element: any) => {
      const divThread = document.querySelector('div[data-thread-id="' + element.threadId + '"]');
      if (divThread && divThread.getAttribute('isComplete') === 'true') {
        element.isComplete = true;
      } else {
        element.isComplete = false;
      }
    });
    /* eslint-enable */

    /* eslint-disable */
    // コメントデータの加工：一時退避していた「添付画像 & リアクション」データをcomment.contentに戻す
    for (let i = 0; i < commentThreadsData.length; i++) {
      const comments = commentThreadsData[i].comments || [];
      for (let j = 0; j < comments.length; j++) {
        const comment = comments[j];
        if (Object.keys(comment).length > 0) {
          const contentItems = [];
          const commentId = comment.commentId;
          // 添付画像のダウンロードリンクの処理
          const commentAttachments = (window as any).commentAttachments || [];
          const commentAttachment = commentAttachments.find((item: any) => item.comment.id === commentId);
          if (commentAttachment) {
            const downloadItems = commentAttachment.downloadItems || [];
            for (let k = 0; k < downloadItems.length; k++) {
              const downloadItem = downloadItems[k];
              const url = downloadItem.url;
              const fileName = downloadItem.fileName;
              contentItems.push(`<p><a href="${url}" target="_blank">${fileName}</a></p>`); // 【重要】過去のデータが存在するのでそのままで！
            }
          }
          // リアクションの処理
          const reactionItems = (window as any).reactionItems || [];
          const targetReactionItems = reactionItems.filter((item: any) => item.commentId === commentId);
          if (targetReactionItems.length > 0) {
            contentItems.push('<user-reaction>');
            contentItems.push(JSON.stringify(targetReactionItems));
            contentItems.push('</user-reaction>');
          }
          if (contentItems.length > 0) {
            // 先頭にcomment.contentを追加
            contentItems.unshift(comment.content || '');
            comment.content = contentItems.join('');
          }
        }
      }
    }
    /* eslint-enable */

    if (revisionTitle !== '') {
      // リビジョン保存モード
      const s = await postSectionMutateAsync(
        {
          sectionId,
          data: {
            ...section,
            edit_content: { ...section?.edit_content, xml_content: editorData },
            revision_json: JSON.stringify(revisionsData),
            comment_thread_json: JSON.stringify(commentThreadsData),
            check_mode: true,
            replace_img_src: repImgSrcArr,
          },
        },
        {
          onSuccess: (response) => response,
          onError: () => {
            if (repImgSrcArr.length > 0) {
              void reverseImgSrc(); // 画像を戻す
            }
            return null;
          },
        }
      );
      if (s !== null) {
        // console.log('リビジョン保存をします。');
        // console.log(s.data);
        // Save the current revision in a const and give it a name
        await revisionTracker.saveRevision({ name: revisionTitle });
        revisionsData = revisionsRepositoryPlugin.getRevisions({
          toJSON: true,
        });
      }
    }

    // console.log('保存ロジック');
    // 通常保存
    await postSectionMutateAsync(
      {
        sectionId,
        data: {
          ...section,
          edit_content: { ...section?.edit_content, xml_content: editorData },
          revision_json: JSON.stringify(revisionsData),
          comment_thread_json: JSON.stringify(commentThreadsData),
          check_mode: false,
          replace_img_src: repImgSrcArr,
        },
      },
      {
        onSuccess: () => {
          void checkLogRefetch(); // ID重複チェック
        },
        onError: () => {
          if (repImgSrcArr.length > 0) {
            void reverseImgSrc();
          }
          void checkLogRefetch(); // ID重複チェック
        },
      }
    );

    if (productionId) {
      const basePath = process.env.REACT_APP_ROUTER_BASEPATH || '';
      window.location.href = `${basePath}/${language}/productions/sections/${String(
        productionId
      )}/ps?section_id=${String(sectionId)}`;
    }
  };

  // #region ID重複ダウンロード
  // API
  const optionsForBinary = useOptionsForBinary(language);
  const {
    mutateAsync: sectionLogDownloadAsync,
    error: sectionLogDownloadError,
    isError: sectionLogDownloadIsError,
    isLoading: sectionLogDownloadIsLoading,
  } = usePostSectionsLogdownload<AxiosError<Blob>>(optionsForBinary);

  // ダウンロードエラーの表示
  const [displaysectionLogDownloadError, setDisplaysectionLogDownloadError] = useState(false);
  useEffect(() => {
    setDisplaysectionLogDownloadError(sectionLogDownloadIsError);
  }, [sectionLogDownloadIsError]);

  // ダウンロード押下時の動き（F-List一覧に渡す）
  const onClickIdLogDownload = useCallback(async () => {
    // ダウンロード API へのアクセス
    await sectionLogDownloadAsync(
      { sectionId },
      {
        // API アクセスが成功したら、ダウンロードを実行
        onSuccess: (response) => executeDownload(response),
      }
    );
  }, [sectionLogDownloadAsync, sectionId]);
  // #endregion

  const defaultOptions = useDefaultOptions(language);
  const loginUserInfo = useGetLoginUserInfo(defaultOptions);
  const user = loginUserInfo.data?.data || {};

  // ログインユーザのシステム権限の有無
  const psLoginUserInfo = useGetLoginUserprivilege(options);
  const isSysUser = useMemo(() => {
    if (psLoginUserInfo === undefined) return false;
    if (psLoginUserInfo.data?.data.privilege_id === undefined) return false;
    if (psLoginUserInfo.data?.data.privilege_id === null) return false;
    return psLoginUserInfo.data?.data.privilege_id.includes(1);
  }, [psLoginUserInfo]);

  // CKEditor置換処理
  interface ReplacementItem {
    typeId: number;
    target: string | null;
    replacement: string;
  }
  const { i18n } = useTranslation();
  const axiosConfig = getAxiosRequestConfig(i18n.language);
  const handleReplaceEditorText = async () => {
    const time = new Date().getTime();
    const url = `${axiosConfig.baseURL}/ckeditor/replacements?time=${time}`;
    const headers = { ...axiosConfig.headers };
    let replacementItems = [];
    try {
      const response = await axios.get(url, { headers });
      replacementItems = response?.data || [];
      if (replacementItems.length === 0) throw new Error('No replacement items');
    } catch (error) {
      alert(t('［エラー］差替えパターンが存在しません。'));
      return;
    }

    let editorText = editor.getData();
    replacementItems.forEach((replacementItem: ReplacementItem) => {
      const { typeId, target, replacement } = replacementItem;
      const targetString = target ?? '';
      if (typeId === 1) {
        // テキスト置換
        editorText = editorText.replaceAll(targetString, replacement);
      } else if (typeId === 2) {
        // 正規表現の置換
        try {
          const regex = new RegExp(targetString, 'g');
          editorText = editorText.replace(regex, replacement);
        } catch (error) {
          console.error('Invalid regex pattern:', error);
        }
      }
    });

    alert(t('差替えを実行しました。'));
    editor.setData(editorText);
  };

  const isReadOnlySection = useMemo(
    () => section?.edit_content?.edit_lock_user_id !== user.user_id,
    [section, user.user_id]
  );

  /**
   * 保存ボタンの有効無効
   */
  const isSaveLock = useMemo(() => {
    if (section === undefined) {
      return true;
    }
    if (section.edit_content?.edit_lock_user_id !== user.user_id || section.save_lock) {
      return true;
    }
    return false;
  }, [section, user.user_id]);

  // ID重複チェック
  const { data: sectionLogData, refetch: checkLogRefetch } = usePostSectionsLogcheck(sectionId, options);

  const uniqId =
    section && `${String(section.chapter_id)}-${String(isReadOnlySection)}-${String(section.l2_section_id)}`;

  // 組版用チェックボックスの制御(最初に一度実行)
  useEffect(() => {
    if (Number(section?.production_division_id) === 2 || Number(section?.production_division_id) === 8) {
      // OM(追補)・翻訳(OM(追補)) => 索引 表示：ON   ビジュアル目次 表示：ON
      setIsDisplayIncludeIndex(true);
      setIsCheckedIncludeIndex(true);
      setIsDisplayIncludeVisual(true);
      setIsCheckedIncludeVisual(true);
    } else if (Number(section?.production_division_id) === 3 || Number(section?.production_division_id) === 9) {
      // Navi・翻訳(Navi) => 索引 非表示：ON   ビジュアル目次 表示：ON
      setIsDisplayIncludeIndex(true);
      setIsCheckedIncludeIndex(true);
      setIsDisplayIncludeVisual(false);
      setIsCheckedIncludeVisual(false);
    } else if (Number(section?.production_division_id) === 13 || Number(section?.production_division_id) === 14) {
      // OG, OG(翻訳) ⇒ 索引 表示：OFF   ビジュアル目次 表示：ON
      setIsDisplayIncludeIndex(true);
      setIsCheckedIncludeIndex(false);
      setIsDisplayIncludeVisual(true);
      setIsCheckedIncludeVisual(true);
    } else {
      // その他 ⇒ 索引 非表示：ON   ビジュアル目次 非表示：ON
      setIsDisplayIncludeIndex(false);
      setIsCheckedIncludeIndex(true);
      setIsDisplayIncludeVisual(false);
      setIsCheckedIncludeVisual(true);
    }
  }, [section]);

  // WebOMプレビューメニューの表示／非表示(navi, 翻訳navi 以外でWebOmプレビューを表示)
  const isShowPreviewWebOm = useMemo(
    () => [1, 2, 4, 5, 6, 7, 8, 10, 11, 12, 13, 14].includes(productionDivisionId),
    [productionDivisionId]
  );

  useEffect(() => {
    if (isSuccess) setDisplaySpinnerModal(false);
  }, [isSuccess]);

  useEffect(() => {
    if (saveError) {
      setIsError(true);
      setDisplaySpinnerModal(false);
    }
  }, [saveError]);

  // 改訂履歴プレビューDIFF
  const RevisionPreviewDiffRef = React.createRef<{ init: () => void }>();
  const switchEditor = (mode: string): void => {
    const editorEl = document.querySelector('#ckeditor');
    const diffEl = document.querySelector('#editor-diff');
    if (editorEl instanceof HTMLElement && diffEl instanceof HTMLElement) {
      if (mode === 'diff') {
        editorEl.style.display = 'none';
        diffEl.style.display = 'block';
        RevisionPreviewDiffRef.current?.init();
      } else {
        editorEl.style.display = 'block';
        diffEl.style.display = 'none';
      }
    }
  };

  // エディタ幅調整
  const defaultCkeditorWidth = localStorage.getItem('ckeditorWidth') || CKEDITOR_WIDTHS.MANU_WIDTH_70_PERCENT;
  const [editorWidth, setEditorWidth] = useState<string>(defaultCkeditorWidth);
  const handleCkeditorWidthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const widthPercent = String(e.target.value);
    setEditorWidth(widthPercent);
    localStorage.setItem('ckeditorWidth', widthPercent);
  };
  const handleResizeEditor = useCallback(() => {
    const mainElement = document.querySelector('#editor-container .ck-editor');
    if (mainElement instanceof HTMLElement) {
      // !important必須
      mainElement.style.setProperty('width', editorWidth, 'important');
      /* eslint-disable */
      const stickyFunction = _.get(editor, 'ui.view.stickyPanel._checkIfShouldBeSticky', null);
      if (stickyFunction && typeof stickyFunction === 'function') {
        stickyFunction();
      }
      /* eslint-enable */
    }
  }, [editor, editorWidth]);
  useEffect(() => {
    window.addEventListener('resize', handleResizeEditor);
  }, [handleResizeEditor]);
  handleResizeEditor();

  if (!section) return null;
  return (
    <>
      <div id="product-manage-main2">
        <div ref={ref}>
          <div className="pmm-head-menu">
            <table className="section-main-menu-table">
              <thead>
                <tr>
                  <td />
                  <td />
                  <td />
                  <td />
                  <td />
                  <td />
                  {isDisplayIncludeVisual && <td />}
                  {isDisplayIncludeIndex && <td />}
                  <td />
                  {(sectionLogData?.data.is_error ?? false) && <td />}
                  <td>
                    <div style={{ textAlign: 'center' }}>
                      <span style={{ marginLeft: '10px' }}>{section.edit_content?.updated_at}</span>
                    </div>
                  </td>
                  {isSysUser && <td />}
                </tr>
                <tr>
                  <td>
                    <div style={{ fontSize: '15px', marginRight: '5px', paddingBottom: '1px' }}>
                      <span style={{ fontWeight: 'bold' }}>{t('資料管理No')}</span>:{' '}
                      <span>{section.production_no}</span>
                    </div>
                  </td>
                  {/* レックスペック連携 */}
                  <td className="modal-open" onClick={() => !isReadOnlySection && openReqSpecImportModal()}>
                    <span
                      className={isReadOnlySection ? 'span-fa-disable' : 'span-fa-enable'}
                      style={{ padding: '5px' }}
                    >
                      <i className="fa-solid fa-link" />
                      {t('RS連携')}
                    </span>
                  </td>
                  {/* 改廃メモ */}
                  <td className="modal-open" onClick={() => !isReadOnlySection && psDerivationId && handleOpenPsNote()}>
                    <span
                      className={!isReadOnlySection && psDerivationId ? 'span-fa-enable' : 'span-fa-disable'}
                      style={{ padding: '5px' }}
                    >
                      <i className="fa-solid fa-pen-nib" />
                      {t('改廃メモ')}
                    </span>
                  </td>
                  {/* PS差異 */}
                  <td>
                    <PsDiffCommand sectionId={sectionId} isReadOnlySection={false} />
                  </td>
                  {/* プレビュー */}
                  <td>
                    <PreviewCommand
                      sectionId={sectionId}
                      isReadOnlySection={false}
                      isShowPreviewWebOm={isShowPreviewWebOm}
                    />
                  </td>
                  {/* PDF出力 */}
                  <td>
                    <DownloadCommand
                      productionId={Number(productionId)}
                      divisionId={Number(section?.production_division_id)}
                      isReadOnlySection={false}
                      includeVisual={isCheckedIncludeVisual}
                      includeIndex={isCheckedIncludeIndex}
                    />
                  </td>
                  {/* ビジュアル目次 */}
                  {isDisplayIncludeVisual && (
                    <td>
                      <span className="ck-left2">
                        <input
                          type="checkbox"
                          id="include_visual"
                          name="include_visual"
                          checked={isCheckedIncludeVisual}
                          onChange={() => setIsCheckedIncludeVisual((prevState) => !prevState)}
                        />{' '}
                        <label htmlFor="include_visual">{t('ビジュアル目次')}</label>
                      </span>
                    </td>
                  )}
                  {/* 索引 */}
                  {isDisplayIncludeIndex && (
                    <td>
                      <span className="ck-left2">
                        <input
                          type="checkbox"
                          id="include_index"
                          name="include_index"
                          checked={isCheckedIncludeIndex}
                          onChange={() => {
                            if (isCheckedIncludeIndex) {
                              if (window.confirm('索引を外してもよろしいですか？')) {
                                setIsCheckedIncludeIndex((prevState) => !prevState);
                              }
                            } else {
                              setIsCheckedIncludeIndex((prevState) => !prevState);
                            }
                          }}
                        />{' '}
                        <label htmlFor="include_index">{t('索引')}</label>
                      </span>
                    </td>
                  )}
                  {/* 改訂履歴 */}
                  <td>
                    <RevisionCommand
                      isReadOnlySection={false}
                      isSaveLock={isSaveLock}
                      editor={editor}
                      setDisplayRevisionSaveModal={setDisplayRevisionSaveModal}
                      switchEditor={switchEditor}
                    />
                  </td>
                  {/* エラーアイコン */}
                  {(sectionLogData?.data.is_error ?? false) && (
                    <td>
                      <button
                        type="button"
                        className="text-button"
                        style={{ color: 'red' }}
                        onClick={() => onClickIdLogDownload()}
                      >
                        <i className="fa-solid fa-circle-check" style={{ color: 'red' }} />
                        {t('エラー')}
                      </button>
                    </td>
                  )}
                  {/* 保存ボタン */}
                  <td>
                    <div>
                      <input
                        type="button"
                        style={{ marginLeft: '15px' }}
                        value={t('保存').toString()}
                        className="searchbtn"
                        onClick={() => handleSave('')}
                        disabled={isSaveLock}
                      />
                    </div>
                  </td>
                  {/* 置換処理 */}
                  {isSysUser && (
                    <td>
                      <div style={{ position: 'relative' }}>
                        <button
                          className="cancelbtn"
                          type="button"
                          style={{ letterSpacing: '0.1em', padding: '0 10px' }}
                          onClick={handleReplaceEditorText}
                        >
                          {t('置換処理')}
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              </thead>
            </table>
            <div>
              <div className="ckeditor-widths-container">
                <select className="ckeditor-widths-select" value={editorWidth} onChange={handleCkeditorWidthChange}>
                  {Object.entries(CKEDITOR_WIDTHS).map(([key, value]) => (
                    <option key={key} value={value}>
                      {t('原稿幅')} {value}
                    </option>
                  ))}
                </select>
                <div className="chevron">
                  <i className="fa-solid fa-chevron-down" />
                </div>
              </div>
            </div>
          </div>
          <div id="ckeditor">
            {section && (
              <SectionEditor
                key={uniqId}
                isReadOnly={isReadOnlySection}
                isSaveLock={isSaveLock}
                section={section}
                sectionPrivilege={sectionPrivilege}
                psNotes={psNotes}
                selectedCharterNumberId={selectedCharterNumberId}
                setSelectedCharterNumberId={setSelectedCharterNumberId}
                setEditor={handleSetEditor}
                setPsDerivationId={setPsDerivationId}
                setSecReading={setSecReading}
              />
            )}
          </div>
          <div id="editor-diff">
            {section && editor && (
              <RevisionPreviewDiff
                ref={RevisionPreviewDiffRef}
                sectionId={sectionId}
                editor={editor}
                switchEditor={switchEditor}
              />
            )}
          </div>
        </div>
      </div>
      <PsNoteFormContainer
        isOpen={isPsNote}
        chapterId={section?.chapter_id || 0}
        psDerivationId={psDerivationId || 0}
        onClose={handleClosePsNote}
        psNoteRefetch={psNoteRefetch}
      />
      <ReqSpecImportModal
        sectionId={sectionId}
        active={showReqSpecImportModal}
        onClose={() => setShowReqSpecImportModal(false)}
      />
      {displaySpinnerModal && <SpinnerModal />}
      {isError && <ErrorModal onClickOk={() => setIsError(false)} error={saveError} />}
      {isNotCommentSaved && (
        <ConfirmModal onClickOk={() => handleSave('', false)} onClickCancel={() => setIsNotCommentSaved(false)}>
          <div style={{ paddingBottom: '2px' }}>{t('エラー')}</div>
          <div style={{ paddingBottom: '10px' }}>
            <small>
              {t('保存されていないコメントが {{count}} 箇所あります。', { count: notCommentSavedElementsLength })}
              <br />
              {t('このまま離脱してもよろしいですか？')}
            </small>
          </div>
        </ConfirmModal>
      )}
      {/* ID重複ダウンロードスピナの表示 */}
      {sectionLogDownloadIsLoading && <SpinnerModal />}
      {/* ID重複ダウンロードエラーの表示 */}
      {displaysectionLogDownloadError && (
        <BlobErrorModal error={sectionLogDownloadError} onClickOk={() => setDisplaysectionLogDownloadError(false)} />
      )}
      {displayRevisionSaveModal && (
        <RevisionSaveModal
          open={displayRevisionSaveModal}
          onClose={() => setDisplayRevisionSaveModal(false)}
          onSave={handleSave} /* モーダルに保存関数を渡す */
        />
      )}
    </>
  );
}
export { SectionMain };
